import React, { ReactNode } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

import { BRAND_COLORS } from "../../../constants";

const ModernDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    backgroundColor: theme.palette.background.paper,
    backgroundImage: "none",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 32px rgba(0,0,0,0.5)"
        : "0 8px 32px rgba(0,0,0,0.12)",
    overflow: "hidden",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0,0,0,0.45)",
  },
}));

const ModernDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: "relative",
  "& .MuiTypography-root": {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: "-0.01em",
    lineHeight: 1.3,
  },
}));

const ModernDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  "&.MuiDialogContent-dividers": {
    borderTop: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const ModernDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(1.5, 3, 2, 3),
  gap: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
  "& .MuiButton-root": {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    padding: theme.spacing(0.875, 2),
    boxShadow: "none",
    "&.MuiButton-contained": {
      backgroundColor: BRAND_COLORS.primary,
      color: "#fff",
      "&:hover": {
        backgroundColor: BRAND_COLORS.primaryDark,
        boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
      },
    },
    "&.MuiButton-outlined": {
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.text.disabled,
      },
    },
    "&.MuiButton-containedError": {
      backgroundColor: BRAND_COLORS.error,
      color: "#fff",
      "&:hover": {
        backgroundColor: BRAND_COLORS.errorDark,
        boxShadow: "0 2px 8px rgba(239,68,68,0.25)",
      },
    },
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1.5),
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.palette.text.secondary,
  width: 32,
  height: 32,
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>,
) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return <Slide direction="up" ref={ref} {...props} />;
  }

  return <Fade ref={ref} {...props} />;
});

interface ModernModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  disableEscapeKeyDown?: boolean;
  disableBackdropClick?: boolean;
  disableCloseButton?: boolean;
  /** Merged into Dialog Paper — use for minHeight, maxHeight, etc. */
  paperSx?: SxProps<Theme>;
}

export default function ModernModal({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "sm",
  fullWidth = true,
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  disableCloseButton = false,
  paperSx,
}: ModernModalProps) {
  const handleClose = (event: any, reason: string) => {
    if (disableBackdropClick && reason === "backdropClick") return;
    if (disableEscapeKeyDown && reason === "escapeKeyDown") return;
    onClose();
  };

  return (
    <ModernDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      TransitionComponent={Transition}
      aria-labelledby="modern-modal-title"
      aria-describedby="modern-modal-description"
      keepMounted={false}
      PaperProps={{ sx: paperSx }}
    >
      <ModernDialogTitle id="modern-modal-title">
        <Typography component="span" variant="h6">
          {title}
        </Typography>
        {!disableCloseButton && (
          <CloseButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </CloseButton>
        )}
      </ModernDialogTitle>

      <ModernDialogContent dividers id="modern-modal-description">
        {children}
      </ModernDialogContent>

      {actions && <ModernDialogActions>{actions}</ModernDialogActions>}
    </ModernDialog>
  );
}
